import { useState } from "react";
import { Trans } from "@lingui/react/macro";
import {
  Button,
  Modal,
  ModalBody,
  ModalContent,
  ModalFooter,
  ModalHeader,
} from "@heroui/react";
import { RiUserLine } from "react-icons/ri";
import dayjs from "dayjs";
import { NoShowNotificationSwitch } from "./NoShowNotificationSwitch";

type NoShowNotificationModalProps = {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: (sendNoShowNotification: boolean) => void;
  hasNoShowNotification: boolean;
  startTime?: string | null;
  customers?: { key: number; name: string; lastName: string }[];
  selectedServices?: { serviceId: number | string }[];
  locationId?: number | null;
};

export const NoShowNotificationModal = ({
  isOpen,
  onClose,
  onConfirm,
  hasNoShowNotification,
  startTime,
  customers,
  selectedServices,
  locationId,
}: NoShowNotificationModalProps) => {
  const [sendNoShowNotification, setSendNoShowNotification] = useState(true);

  // Reset on close, otherwise an off toggle from a previous no-show silently
  // suppresses the next notification.
  const handleClose = () => {
    setSendNoShowNotification(true);
    onClose();
  };

  return (
    <Modal isOpen={isOpen} onClose={handleClose}>
      <ModalContent>
        <ModalHeader>
          <Trans>Označi neprihod</Trans>
        </ModalHeader>
        <ModalBody className="gap-5">
          {startTime ? (
            <div className="bg-content2 rounded-large flex items-center justify-between gap-3 p-3">
              <span className="text-default-500 text-xs">
                {dayjs(startTime).format("dd, DD.MM.YYYY")}
              </span>
              <span className="text-sm leading-5 font-semibold">
                {dayjs(startTime).format("HH:mm")}
              </span>
            </div>
          ) : null}

          {customers?.length ? (
            <div className="flex flex-col gap-2">
              <span className="text-default-500 text-xs font-medium">
                <Trans>Neprihod označujete za naslednje stranke</Trans>
              </span>

              <div className="flex flex-wrap gap-1.5">
                {customers.map((customer) => (
                  <span
                    className="bg-content2 flex items-center gap-1.5 rounded-full py-1 pr-3 pl-2 text-sm"
                    key={customer.key}
                  >
                    <RiUserLine
                      className="text-default-500 shrink-0"
                      size={".85rem"}
                    />
                    {customer.name} {customer.lastName}
                  </span>
                ))}
              </div>
            </div>
          ) : null}

          {hasNoShowNotification ? (
            <NoShowNotificationSwitch
              isSelected={sendNoShowNotification}
              onValueChange={setSendNoShowNotification}
              selectedServices={selectedServices}
              locationId={locationId}
            />
          ) : null}
        </ModalBody>
        <ModalFooter>
          <Button variant="flat" onPress={handleClose}>
            <Trans>Prekliči</Trans>
          </Button>
          <Button
            color="primary"
            data-identifier="confirm-no-show"
            onPress={() => {
              onConfirm(sendNoShowNotification);
              handleClose();
            }}
          >
            <Trans>Potrdi</Trans>
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};
